import { Maybe } from '@whistler-ui/k2/types';
import dayjs from 'dayjs';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { SETTINGS } from '../../constants';

export const TABS = {
  overview: 'overview',
  transfer: 'Device_Transfer',
  watch: 'Watch_Activation',
  rcs: 'RCS',
} as const;

export type ReportTab = (typeof TABS)[keyof typeof TABS];

// ─── Global filter — single source of truth for all tabs ─────────────────────
// platform: 'all' is valid but only overview renders it; other tabs fall back to 'ios'
export interface GlobalFilter {
  startDate: Date;
  endDate: Date;
  rangeOption: number;
  platform: 'ios' | 'android' | 'all';
}

// ─── Per-tab filters ──────────────────────────────────────────────────────────
// Overview and Device_Transfer have no tab-specific fields — platform is global.
// Watch_Activation adds 'action' (the flow type).
export interface WatchActivationTabFilter {
  action?: 'watch_activation' | 'watch_transfer';
}

export interface RCSTabFilter {
  days?: number;
}

export type TabFilterMap = {
  overview: Record<string, never>;
  Device_Transfer: Record<string, never>;
  Watch_Activation: WatchActivationTabFilter;
  RCS: RCSTabFilter;
};

// ─── Merged type — shape returned by getCurrentFilter() ──────────────────────
// For non-overview tabs: platform will never be 'all' (fallback applied inside getter)
// For RCS tab: platform is omitted — RCS only uses startDate, endDate, rangeOption
export type Filter = Omit<GlobalFilter, 'platform'> & {
  platform?: GlobalFilter['platform'];
  action?: 'watch_activation' | 'watch_transfer';
  days?: number;
};

/** Partial tab-specific fields accepted by updateTabFilter */
export type TabFilterUpdate =
  | Partial<WatchActivationTabFilter>
  | Partial<RCSTabFilter>;

export interface ReportLayout {
  stepByStepTableOpen: boolean;
}

// ─── Store state shape ────────────────────────────────────────────────────────
interface ReportsStore {
  selectedTab: Maybe<ReportTab>;
  layout: Partial<Record<ReportTab, ReportLayout>>;
  globalFilter: GlobalFilter;
  filters: Partial<TabFilterMap>;
  validTabs: ReportTab[];
}

// ─── Default values ───────────────────────────────────────────────────────────
const defaultGlobalFilter: GlobalFilter = {
  startDate: dayjs().subtract(6, 'day').startOf('day').toDate(),
  endDate: dayjs().endOf('day').toDate(),
  rangeOption: 0,
  platform: 'all', // overview default; other tabs will fall back to 'ios' via getCurrentFilter
};

const defaultTabFilters: Partial<TabFilterMap> = {
  Watch_Activation: { action: 'watch_activation' },
};

const initialValues: ReportsStore = {
  selectedTab: 'overview',
  layout: {
    Device_Transfer: { stepByStepTableOpen: false },
    Watch_Activation: { stepByStepTableOpen: false },
  },
  globalFilter: defaultGlobalFilter,
  filters: defaultTabFilters,
  validTabs: Object.values(TABS),
};

// ─── Store actions ────────────────────────────────────────────────────────────
type Store = {
  values: ReportsStore;
  setReportsValues: (newValues: Partial<ReportsStore>) => void;
  setNewReportTab: (tab: string) => void;
  /** Update the global filter (dates, rangeOption, platform) — affects all tabs */
  updateGlobalFilter: (updates: Partial<GlobalFilter>) => void;
  /** Update the tab-specific filter for the currently active tab (Watch_Activation only has 'action') */
  updateTabFilter: (updates: TabFilterUpdate) => void;
  /**
   * Returns the merged filter for the current tab.
   * Platform fallback: if global platform is 'all' and the active tab is not 'overview', returns 'ios'.
   */
  getCurrentFilter: () => Filter | undefined;
  getCurrentLayoutValues: () => ReportLayout | undefined;
  setLayoutValues: (newValues: Partial<ReportLayout>) => void;
};

export const useReportsStore = create<Store>()(
  devtools(
    (set, get) => ({
      values: initialValues,

      setReportsValues: newValues => {
        set(
          state => ({ values: { ...state.values, ...newValues } }),
          false,
          'useReportsStore/setReportsValues',
        );
      },

      setNewReportTab: newTab => {
        set(
          state => {
            const tab = newTab as ReportTab;
            const updatedFilters = { ...state.values.filters };

            if (!updatedFilters[tab]) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              updatedFilters[tab] = (defaultTabFilters[tab] ?? {}) as any;
            }

            // Normalize 'all' → 'ios' when leaving overview
            // 'all' is not a real platform value — collapse it so all tabs stay in sync
            const globalFilter =
              tab !== TABS.overview &&
              state.values.globalFilter.platform === 'all'
                ? { ...state.values.globalFilter, platform: 'ios' as const }
                : state.values.globalFilter;

            return {
              values: {
                ...state.values,
                selectedTab: tab,
                filters: updatedFilters,
                globalFilter,
              },
            };
          },
          false,
          'useReportsStore/setNewReportTab',
        );
      },

      updateGlobalFilter: (updates: Partial<GlobalFilter>) => {
        set(
          state => ({
            values: {
              ...state.values,
              globalFilter: { ...state.values.globalFilter, ...updates },
            },
          }),
          false,
          'useReportsStore/updateGlobalFilter',
        );
      },

      updateTabFilter: (updates: TabFilterUpdate) => {
        set(
          state => {
            const selectedTab = state.values.selectedTab;
            if (!selectedTab) return state;

            return {
              values: {
                ...state.values,
                filters: {
                  ...state.values.filters,
                  [selectedTab]: {
                    ...state.values.filters[selectedTab],
                    ...updates,
                  },
                },
              },
            };
          },
          false,
          'useReportsStore/updateTabFilter',
        );
      },

      getCurrentFilter: () => {
        const { values } = get();
        const { selectedTab, globalFilter, filters } = values;
        if (!selectedTab) return undefined;

        // RCS only uses date range + its own days filter — no platform
        if (selectedTab === TABS.rcs) {
          return {
            startDate: globalFilter.startDate,
            endDate: globalFilter.endDate,
            rangeOption: globalFilter.rangeOption,
            ...filters[TABS.rcs],
          } as Filter;
        }

        // Non-overview tabs cannot show 'all' — fall back to 'ios'
        const platform =
          selectedTab !== TABS.overview && globalFilter.platform === 'all'
            ? 'ios'
            : globalFilter.platform;

        return {
          ...globalFilter,
          platform,
          ...filters[selectedTab],
        } as Filter;
      },

      getCurrentLayoutValues: () => {
        const { values } = get();
        return values.layout[values.selectedTab];
      },

      setLayoutValues: (newValues: Partial<ReportLayout>) => {
        set(
          state => ({
            values: {
              ...state.values,
              layout: {
                ...state.values.layout,
                [state.values.selectedTab as ReportTab]: {
                  ...state.values.layout[state.values.selectedTab as ReportTab],
                  ...newValues,
                },
              },
            },
          }),
          false,
          'useReportsStore/setLayoutValues',
        );
      },
    }),
    {
      name: 'useReportsStore',
      enabled: SETTINGS.IS_DEV,
    },
  ),
);
